import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Ticket, X, Share2, Download } from 'lucide-react'
import { toPng } from 'html-to-image'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'
import type { TarotDrawResponse } from '../lib/api'
import { liff } from '../lib/liff'
import { TarotShareCard } from '../components/TarotShareCard'

const THAI_MONTHS_FULL = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม']

const ROMAN: Record<number, string> = {
  0: '0', 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII',
  8: 'VIII', 9: 'IX', 10: 'X', 11: 'XI', 12: 'XII', 13: 'XIII', 14: 'XIV',
  15: 'XV', 16: 'XVI', 17: 'XVII', 18: 'XVIII', 19: 'XIX', 20: 'XX', 21: 'XXI',
}

function formatThaiDateFull(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  return `${d} ${THAI_MONTHS_FULL[m - 1]} ${y + 543}`
}

function OrnateCorner({ rotation = 0 }: { rotation?: 0 | 90 | 180 | 270 }) {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" style={{ transform: `rotate(${rotation}deg)` }}>
      <g stroke="#8b6f3a" strokeWidth="1.1" fill="none" strokeLinecap="round">
        <path d="M 3 3 L 14 3" />
        <path d="M 3 3 L 3 14" />
        <path d="M 7 3 Q 11 7 7 11" />
        <path d="M 3 7 Q 7 7 7 11" />
        <circle cx="3" cy="3" r="1.5" fill="#b8945a" stroke="none" />
        <circle cx="14" cy="3" r="0.8" fill="#8b6f3a" stroke="none" />
        <circle cx="3" cy="14" r="0.8" fill="#8b6f3a" stroke="none" />
      </g>
    </svg>
  )
}

function FleurDivider({ light = false }: { light?: boolean }) {
  const color = light ? 'rgba(184,148,90,0.6)' : '#8b6f3a'
  return (
    <div className="flex items-center gap-1.5 my-1 w-full">
      <div className="h-px flex-1" style={{ background: `linear-gradient(to right, transparent, ${color}, transparent)` }} />
      <span style={{ color, fontSize: 9 }}>❖</span>
      <div className="h-px flex-1" style={{ background: `linear-gradient(to right, transparent, ${color}, transparent)` }} />
    </div>
  )
}

function CardBackArt({ flipping }: { flipping: boolean }) {
  return (
    <div className="relative w-[220px] h-[340px] rounded-xl mx-auto"
         style={{
           background: 'radial-gradient(ellipse at center, #2e2153 0%, #1a1135 70%, #0f0820 100%)',
           boxShadow: `
             0 0 0 1px #b8945a,
             0 0 0 4px #1a1135,
             0 0 0 5px #b8945a,
             0 25px 60px rgba(0,0,0,0.55)
           `,
           transform: flipping ? 'rotateY(180deg)' : 'rotateY(0deg)',
           transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
         }}>
      {/* Repeating mystical star pattern */}
      <div className="absolute inset-0 opacity-30 rounded-xl"
           style={{
             backgroundImage: `
               radial-gradient(circle at 20% 15%, rgba(212,168,104,0.55) 0, transparent 1.5%),
               radial-gradient(circle at 80% 15%, rgba(212,168,104,0.55) 0, transparent 1.5%),
               radial-gradient(circle at 20% 50%, rgba(212,168,104,0.4) 0, transparent 1.5%),
               radial-gradient(circle at 80% 50%, rgba(212,168,104,0.4) 0, transparent 1.5%),
               radial-gradient(circle at 20% 85%, rgba(212,168,104,0.55) 0, transparent 1.5%),
               radial-gradient(circle at 80% 85%, rgba(212,168,104,0.55) 0, transparent 1.5%),
               radial-gradient(circle at 50% 30%, rgba(212,168,104,0.3) 0, transparent 1%),
               radial-gradient(circle at 50% 70%, rgba(212,168,104,0.3) 0, transparent 1%)
             `,
           }} />

      {/* Top + bottom corner stars */}
      <div className="absolute top-2 left-3 text-[10px]" style={{ color: '#d4a868' }}>✦</div>
      <div className="absolute top-2 right-3 text-[10px]" style={{ color: '#d4a868' }}>✦</div>
      <div className="absolute bottom-2 left-3 text-[10px]" style={{ color: '#d4a868' }}>✦</div>
      <div className="absolute bottom-2 right-3 text-[10px]" style={{ color: '#d4a868' }}>✦</div>

      {/* Center medallion */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          <div className="w-36 h-36 rounded-full flex items-center justify-center"
               style={{
                 border: '2px solid #b8945a',
                 boxShadow: '0 0 35px rgba(184,148,90,0.45), inset 0 0 25px rgba(184,148,90,0.18)',
               }}>
            <div className="w-28 h-28 rounded-full flex flex-col items-center justify-center"
                 style={{ border: '1px solid rgba(184,148,90,0.55)' }}>
              <div className="text-5xl mb-0.5">🪬</div>
              <div className="text-[9px] tracking-[0.35em] font-bold mt-1"
                   style={{ color: '#d4a868', fontFamily: 'Georgia, serif' }}>
                AMULET
              </div>
              <div className="text-[8px] tracking-[0.45em] mt-0.5"
                   style={{ color: '#b8945a', fontFamily: 'Georgia, serif' }}>
                TAROT
              </div>
            </div>
          </div>
          <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-sm" style={{ color: '#d4a868' }}>✦</span>
          <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-sm" style={{ color: '#d4a868' }}>✦</span>
          <span className="absolute top-1/2 -translate-y-1/2 -left-7 text-sm" style={{ color: '#d4a868' }}>✧</span>
          <span className="absolute top-1/2 -translate-y-1/2 -right-7 text-sm" style={{ color: '#d4a868' }}>✧</span>
        </div>
      </div>

      {/* Top header ornament */}
      <div className="absolute top-4 left-0 right-0 text-center text-[9px] tracking-[0.5em]" style={{ color: 'rgba(184,148,90,0.75)' }}>
        ✦ ❖ ✦
      </div>
      <div className="absolute bottom-4 left-0 right-0 text-center text-[9px] tracking-[0.5em]" style={{ color: 'rgba(184,148,90,0.75)' }}>
        ✦ ❖ ✦
      </div>
    </div>
  )
}

function CardFrontArt({ card, isReversed }: {
  card: TarotDrawResponse['card']
  isReversed: boolean
}) {
  return (
    <div className="relative w-[240px] h-[420px] rounded-xl mx-auto overflow-hidden"
         style={{
           background: 'linear-gradient(180deg, #f7ecd0 0%, #ecdfb8 100%)',
           boxShadow: `
             0 0 0 1px #6b5226,
             0 0 0 5px #f5ebd0,
             0 0 0 7px #b8945a,
             0 25px 60px rgba(0,0,0,0.35)
           `,
         }}>
      {/* Image area — full-bleed with letterboxing */}
      <div className="absolute inset-0 flex items-center justify-center"
           style={{
             background: 'radial-gradient(ellipse at center, #f7ecd0 0%, #d9c896 100%)',
           }}>
        {card.image_url ? (
          <img
            src={card.image_url}
            alt={card.name_th}
            crossOrigin="anonymous"
            className={`w-full h-full object-contain ${isReversed ? 'rotate-180' : ''}`}
            style={{ filter: 'drop-shadow(0 4px 8px rgba(107,82,38,0.25))' }}
          />
        ) : (
          <div className={`text-8xl ${isReversed ? 'rotate-180' : ''}`}>
            {card.emoji ?? '🃏'}
          </div>
        )}
      </div>

      {/* Roman numeral badge — top */}
      <div className="absolute top-2 left-0 right-0 flex justify-center pointer-events-none">
        <div className="px-3 py-0.5 rounded-full"
             style={{
               background: 'rgba(247,236,208,0.94)',
               border: '1px solid #b8945a',
               boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
             }}>
          <div className="text-xs font-bold tracking-[0.3em]"
               style={{ color: '#6b5226', fontFamily: 'Georgia, serif' }}>
            {ROMAN[card.position]}
          </div>
        </div>
      </div>

      {/* Title plate — bottom overlay */}
      <div className="absolute bottom-0 left-0 right-0 pt-10 pb-2.5 px-3 text-center pointer-events-none"
           style={{
             background: 'linear-gradient(to top, rgba(26,17,53,0.95) 35%, rgba(26,17,53,0.7) 65%, transparent 100%)',
           }}>
        <FleurDivider light />
        <div className="font-bold text-lg leading-tight mt-1"
             style={{
               color: '#f7ecd0',
               fontFamily: 'Georgia, "Cormorant Garamond", serif',
               textShadow: '0 2px 6px rgba(0,0,0,0.7)',
             }}>
          {card.name_th}
        </div>
        <div className="text-[10px] tracking-[0.35em] mt-0.5 font-bold"
             style={{ color: '#d4a868', fontFamily: 'Georgia, serif' }}>
          {card.name_en.toUpperCase()}
        </div>
      </div>

      {/* Corner flourishes (top layer) */}
      <div className="absolute top-2 left-2 pointer-events-none"><OrnateCorner rotation={0} /></div>
      <div className="absolute top-2 right-2 pointer-events-none"><OrnateCorner rotation={90} /></div>
    </div>
  )
}

interface TodayPayload {
  draw: TarotDrawResponse | null
  drawn: boolean
  tickets: number
}

function isMetaWrapped(value: unknown): value is { meta?: { drawn?: boolean; tickets?: number } } {
  return typeof value === 'object' && value !== null
}

export default function TarotPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { lineUid, profile } = useAppStore()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [drawing, setDrawing] = useState(false)
  const [flipping, setFlipping] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [justDrew, setJustDrew] = useState(false)
  const [showShare, setShowShare] = useState(false)

  const todayQuery = useQuery({
    queryKey: ['tarot', 'today', lineUid],
    queryFn: async () => {
      const res = await api.tarot.today(lineUid!) as unknown
      let draw: TarotDrawResponse | null = null
      let drawn = false
      let tickets = 0
      if (isMetaWrapped(res)) {
        drawn = !!res.meta?.drawn
        tickets = res.meta?.tickets ?? 0
        // unwrap data (request() merges data+meta when meta present)
        const cloned = { ...(res as Record<string, unknown>) }
        delete cloned.meta
        if (drawn && cloned.id) draw = cloned as unknown as TarotDrawResponse
      }
      return { draw, drawn, tickets } as TodayPayload
    },
    enabled: !!lineUid,
    staleTime: 1000 * 30,
  })

  const tickets = todayQuery.data?.tickets ?? (profile?.dream_quota?.paid_tickets ?? 0)
  const currentDraw = todayQuery.data?.draw ?? null
  const alreadyDrawn = !!currentDraw

  async function handleDraw() {
    if (!lineUid || drawing) return
    setDrawing(true)
    setErrorMsg(null)
    setFlipping(true)
    try {
      const res = await api.tarot.draw(lineUid)
      // brief flip animation pause
      await new Promise(r => setTimeout(r, 700))
      queryClient.setQueryData<TodayPayload>(['tarot', 'today', lineUid], {
        draw: res,
        drawn: true,
        tickets: Math.max(0, tickets - 1),
      })
      setJustDrew(true)
      setConfirmOpen(false)
    } catch (err) {
      const e = err as Error & { code?: string }
      if (e.code === 'no_ticket') setErrorMsg('ไม่มีตั๋วเหลือ — เช็คอินรายวันรับฟรี หรือซื้อเพิ่ม')
      else setErrorMsg(e.message ?? 'เกิดข้อผิดพลาด กรุณาลองใหม่')
    } finally {
      setDrawing(false)
      setFlipping(false)
    }
  }

  if (todayQuery.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh gap-3"
           style={{ background: 'var(--color-bg)' }}>
        <div className="text-5xl animate-pulse">🃏</div>
        <p style={{ color: 'var(--color-text-muted)' }}>กำลังเปิดไพ่...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-dvh pb-6"
         style={{ background: 'var(--color-bg)' }}>
      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button onClick={() => navigate('/')}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-3)' }}>
          <ChevronLeft size={20} style={{ color: 'var(--color-text-muted)' }} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold" style={{ color: 'var(--color-gold-dark)' }}>
            🃏 ไพ่ยิปซีวันนี้
          </h1>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Major Arcana — สุ่ม 1 ใบ/วัน
          </p>
        </div>
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full"
             style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-3)' }}>
          <Ticket size={14} style={{ color: 'var(--color-gold-dark)' }} />
          <span className="text-xs font-bold" style={{ color: 'var(--color-text)' }}>{tickets}</span>
        </div>
      </div>

      {!alreadyDrawn ? (
        <DrawIntro
          flipping={flipping}
          tickets={tickets}
          onDraw={() => {
            setErrorMsg(null)
            setConfirmOpen(true)
          }}
        />
      ) : (
        <CardReveal draw={currentDraw!} highlight={justDrew} onShare={() => setShowShare(true)} />
      )}

      {errorMsg && (
        <div className="mx-4 mt-2 mb-3 px-4 py-3 rounded-xl text-sm"
             style={{ background: 'rgba(229,62,62,0.12)', color: '#c53030', border: '1px solid rgba(229,62,62,0.3)' }}>
          {errorMsg}
        </div>
      )}

      <div className="mt-auto pt-6 pb-4 px-4">
        <p className="text-center text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
          เพื่อความบันเทิงและข้อมูลเชิงสถิติเท่านั้น
        </p>
      </div>

      {confirmOpen && (
        <ConfirmDrawModal
          tickets={tickets}
          drawing={drawing}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleDraw}
          onBuy={() => navigate('/store')}
        />
      )}

      {showShare && currentDraw && (
        <ShareImageModal data={currentDraw} onClose={() => setShowShare(false)} />
      )}
    </div>
  )
}

function DrawIntro({ tickets, flipping, onDraw }: {
  tickets: number
  flipping: boolean
  onDraw: () => void
}) {
  return (
    <div className="flex flex-col items-center px-4">
      {/* Card back */}
      <div className="my-6">
        <CardBackArt flipping={flipping} />
      </div>

      <h2 className="text-xl font-bold text-center mb-1.5"
          style={{ color: 'var(--color-text)' }}>
        เปิดไพ่ของวันนี้
      </h2>
      <p className="text-sm text-center mb-6 max-w-xs"
         style={{ color: 'var(--color-text-muted)' }}>
        เลือก 1 ใบจาก Major Arcana 22 ใบ
        <br />ดูข้อความสำหรับวันนี้และเลขมงคล
      </p>

      <button
        onClick={onDraw}
        disabled={flipping}
        className="w-full max-w-xs py-4 rounded-2xl font-bold text-base btn-gold shimmer disabled:opacity-60"
        style={{ boxShadow: '0 8px 24px rgba(184,148,90,0.4)' }}
      >
        ✨ เปิดไพ่ — ใช้ 1 ตั๋ว
      </button>

      <p className="text-[11px] mt-3" style={{ color: 'var(--color-text-soft)' }}>
        คุณมี {tickets} ตั๋ว
      </p>
    </div>
  )
}

function CardReveal({ draw, highlight, onShare }: {
  draw: TarotDrawResponse
  highlight: boolean
  onShare: () => void
}) {
  const { card, orientation } = draw
  const isReversed = orientation === 'reversed'

  return (
    <div className="px-4">
      {/* Card hero */}
      <div className={`mx-auto mb-4 ${highlight ? 'animate-[pulse_1.5s_ease-in-out_1]' : ''}`}>
        <CardFrontArt card={card} isReversed={isReversed} />
      </div>

      {/* Keywords */}
      {card.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5 justify-center mb-4">
          {card.keywords.map(k => (
            <span key={k}
                  className="text-[11px] px-2.5 py-1 rounded-full font-medium"
                  style={{
                    background: 'var(--color-surface-2)',
                    color: 'var(--color-text-muted)',
                    border: '1px solid var(--color-surface-3)',
                  }}>
              {k}
            </span>
          ))}
        </div>
      )}

      {/* Meaning */}
      <div className="card-amulet p-4 mb-4"
           style={{ borderLeft: '3px solid var(--color-gold)' }}>
        <div className="text-[10px] uppercase tracking-widest mb-2"
             style={{ color: 'var(--color-gold-dark)' }}>
          ✨ ข้อความสำหรับวันนี้
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-line"
           style={{ color: 'var(--color-text)' }}>
          {card.meaning}
        </p>
      </div>

      {/* Share button */}
      <button
        onClick={onShare}
        className="w-full mb-3 py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-transform active:scale-[0.98] btn-gold shimmer"
        style={{ boxShadow: '0 4px 14px rgba(184,148,90,0.35)' }}
      >
        <Share2 size={16} />
        แชร์ไพ่วันนี้
      </button>

      {/* Refresh hint */}
      <p className="text-center text-[11px] mb-4"
         style={{ color: 'var(--color-text-soft)' }}>
        📅 {formatThaiDateFull(draw.draw_date)} · พรุ่งนี้มาเปิดไพ่ใหม่
      </p>
    </div>
  )
}

function ConfirmDrawModal({ tickets, drawing, onCancel, onConfirm, onBuy }: {
  tickets: number
  drawing: boolean
  onCancel: () => void
  onConfirm: () => void
  onBuy: () => void
}) {
  const hasTicket = tickets > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.55)' }}
         onClick={onCancel}>
      <div className="w-full max-w-sm rounded-3xl overflow-hidden"
           style={{
             background: 'var(--color-surface)',
             marginBottom: 'calc(76px + env(safe-area-inset-bottom))',
           }}
           onClick={e => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
              {hasTicket ? 'ยืนยันเปิดไพ่?' : 'ไม่มีตั๋วเหลือ'}
            </h3>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {hasTicket
                ? 'ใช้ 1 ตั๋ว สุ่ม 1 ใบจาก Major Arcana 22 ใบ'
                : 'เช็คอินรายวันรับฟรี หรือซื้อตั๋วเพิ่ม'}
            </p>
          </div>
          <button onClick={onCancel}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--color-surface-2)' }}>
            <X size={14} style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="px-5 pb-3 flex items-center gap-2">
          <Ticket size={14} style={{ color: 'var(--color-gold-dark)' }} />
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            ตั๋วคงเหลือ <span className="font-bold" style={{ color: 'var(--color-text)' }}>{tickets}</span> ใบ
          </span>
        </div>

        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onCancel}
                  disabled={drawing}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm disabled:opacity-60"
                  style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            ยกเลิก
          </button>
          {hasTicket ? (
            <button onClick={onConfirm}
                    disabled={drawing}
                    className="flex-1 py-3 rounded-xl font-bold text-sm btn-gold disabled:opacity-60">
              {drawing ? 'กำลังเปิด...' : 'ยืนยัน ✨'}
            </button>
          ) : (
            <button onClick={onBuy}
                    className="flex-1 py-3 rounded-xl font-bold text-sm btn-gold">
              ซื้อตั๋ว 9฿
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Social media SVG logos ────────────────────────────────
function FbLogo() {
  return (
    <svg viewBox="0 0 32 32" width="30" height="30">
      <rect width="32" height="32" rx="8" fill="#1877F2"/>
      <path d="M21 10h-3c-.6 0-1 .4-1 1v2h4l-.5 3H17v8h-3v-8h-2v-3h2v-2c0-2.8 1.7-4 4.3-4H21v3z" fill="white"/>
    </svg>
  )
}
function IgLogo() {
  return (
    <svg viewBox="0 0 32 32" width="30" height="30">
      <defs>
        <linearGradient id="ig-tarot" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F58529"/>
          <stop offset="40%" stopColor="#DD2A7B"/>
          <stop offset="80%" stopColor="#8134AF"/>
          <stop offset="100%" stopColor="#515BD4"/>
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#ig-tarot)"/>
      <rect x="7" y="7" width="18" height="18" rx="5" stroke="white" strokeWidth="1.8" fill="none"/>
      <circle cx="16" cy="16" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
      <circle cx="22" cy="10" r="1.3" fill="white"/>
    </svg>
  )
}
function LineLogo() {
  return (
    <svg viewBox="0 0 32 32" width="30" height="30">
      <rect width="32" height="32" rx="8" fill="#06C755"/>
      <path d="M26 14.8C26 9.9 21.1 6 15 6S4 9.9 4 14.8c0 4.4 3.9 8 9.2 8.7.4.1.9.3 1 .6.1.3.1.8 0 1.1l-.2 1c0 .3-.2 1.1 1 .6C16.3 26 22 22.3 24.5 19.4 26.2 17.7 26 16.3 26 14.8z" fill="white"/>
    </svg>
  )
}

function ShareImageModal({ data, onClose }: { data: TarotDrawResponse; onClose: () => void }) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [cardImageDataUrl, setCardImageDataUrl] = useState<string | null>(null)
  const [generating, setGenerating] = useState(true)
  const [fullscreen, setFullscreen] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [lineSharing, setLineSharing] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  // ── Prepare card image as dataURL → capture full share card ──
  // Use fresh Image() with crossOrigin='anonymous' + cache-buster query string
  // to bypass any stale browser cache that doesn't have CORS headers.
  useEffect(() => {
    let cancelled = false

    function loadImageAsDataUrl(url: string): Promise<string | null> {
      return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) return resolve(null)
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/png'))
          } catch (e) {
            console.warn('[Tarot share] canvas readback failed', e)
            resolve(null)
          }
        }
        img.onerror = () => resolve(null)
        // Cache-buster: force fresh fetch with CORS headers (bypasses no-cors cache)
        img.src = url + (url.includes('?') ? '&' : '?') + 'cors=1'
      })
    }

    async function prepareAndCapture() {
      // Step 1: load card image with CORS → dataURL
      if (data.card.image_url) {
        const final = await loadImageAsDataUrl(data.card.image_url)
        if (cancelled) return
        setCardImageDataUrl(final)
      }

      // Step 2: small delay → re-render with dataURL → toPng
      await new Promise(r => setTimeout(r, 350))
      if (cancelled || !cardRef.current) return
      try {
        const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: false })
        if (!cancelled) setImgUrl(dataUrl)
      } catch {
        if (!cancelled) setImgUrl(null)
      } finally {
        if (!cancelled) setGenerating(false)
      }
    }

    prepareAndCapture()
    return () => { cancelled = true }
  }, [data.card.image_url])

  const getFile = async (): Promise<File | null> => {
    if (!imgUrl) return null
    const res = await fetch(imgUrl)
    const blob = await res.blob()
    return new File([blob], `tarot-${data.card.slug}-${data.draw_date}.png`, { type: 'image/png' })
  }

  const handleDownload = () => {
    if (!imgUrl) return
    const a = document.createElement('a')
    a.href = imgUrl
    a.download = `tarot-${data.card.slug}-${data.draw_date}.png`
    a.click()
  }

  const handleNativeShare = async () => {
    setSharing(true)
    try {
      const file = await getFile()
      if (!file) { showToast('ยังสร้างภาพไม่เสร็จ'); return }
      const title = `🃏 ${data.card.name_th} — ${data.orientation === 'reversed' ? 'ไพ่กลับหัว' : 'ไพ่หงาย'}`
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title,
          text: `ไพ่ยิปซีของวันนี้จาก Amulet Digit\nhttps://www.amuletdigit.com`,
        })
      } else {
        await navigator.share({
          title,
          text: `ไพ่ยิปซีของวันนี้จาก Amulet Digit`,
          url: 'https://www.amuletdigit.com',
        }).catch(() => handleDownload())
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name !== 'AbortError') showToast('แชร์ไม่สำเร็จ')
    } finally {
      setSharing(false)
    }
  }

  const handleLineShare = async () => {
    setLineSharing(true)
    try {
      if (!liff.isInClient()) {
        showToast('ใช้ได้เฉพาะใน LINE เท่านั้น')
        return
      }
      const { card, orientation, draw_date } = data
      const isReversed = orientation === 'reversed'
      const orientLabel = isReversed ? 'ไพ่กลับหัว' : 'ไพ่หงาย'
      const orientColor = isReversed ? '#e07090' : '#d4b884'
      const dateStr = (() => {
        const d = new Date(draw_date + 'T00:00:00')
        const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์']
        const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
        return `วัน${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
      })()
      const liffUrl = 'https://liff.line.me/2009976171-SBRuplHE'
      const imageUrl = card.image_url
        ? `https://www.amuletdigit.com${card.image_url}`
        : `https://www.amuletdigit.com/logo.png`

      const result = await liff.shareTargetPicker(
        [{
          type: 'flex',
          altText: `🃏 ${card.name_th} (${orientLabel}) — ไพ่ยิปซีจาก Amulet Digit`,
          contents: {
            type: 'bubble',
            hero: {
              type: 'image',
              url: imageUrl,
              size: 'full',
              aspectRatio: '1:1.5',
              aspectMode: 'cover',
              backgroundColor: '#f7ecd0',
            },
            body: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#1c1810', paddingAll: '16px', spacing: 'sm',
              contents: [
                { type: 'text', text: dateStr, color: '#b8945a', size: 'sm', align: 'center', weight: 'bold' },
                {
                  type: 'box', layout: 'horizontal', margin: 'md', spacing: 'sm',
                  contents: [
                    { type: 'text', text: card.name_th, color: '#f0ead8', size: 'lg', weight: 'bold', flex: 1, wrap: true },
                    { type: 'text', text: orientLabel, color: orientColor, size: 'xs', weight: 'bold', align: 'end', gravity: 'center', flex: 0 },
                  ],
                },
                { type: 'text', text: card.name_en.toUpperCase(), color: '#7a7260', size: 'xxs', weight: 'bold' },
                { type: 'separator', margin: 'md', color: '#2d2215' },
                { type: 'text', text: '✨ ข้อความสำหรับวันนี้', color: '#d4b884', size: 'xs', weight: 'bold', margin: 'md' },
                { type: 'text', text: card.meaning, color: '#b8b09a', size: 'xs', wrap: true, margin: 'sm' },
              ],
            },
            footer: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#16120d', paddingAll: '12px',
              contents: [{
                type: 'button',
                action: { type: 'uri', label: 'เปิดไพ่ของฉัน 🃏', uri: liffUrl },
                style: 'primary', color: '#b8945a', height: 'sm',
              }],
            },
          },
        }],
        { isMultiple: false }
      )
      if (result?.status === 'success') showToast('ส่งให้เพื่อนแล้ว ✨')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      if (e instanceof Error && e.name === 'AbortError') return
      if (msg.includes('not supported') || msg.includes('permission') || msg.includes('shareTargetPicker')) {
        showToast('กรุณาเปิด Share Target Picker ใน LINE Console ก่อน')
      } else {
        showToast(`LINE: ${msg.slice(0, 60)}`)
      }
    } finally {
      setLineSharing(false)
    }
  }

  return (
    <>
      {fullscreen && imgUrl && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center"
             style={{ background: 'rgba(0,0,0,0.95)' }} onClick={() => setFullscreen(false)}>
          <button className="absolute top-5 right-5 z-10 p-2 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.15)' }} onClick={() => setFullscreen(false)}>
            <X size={24} color="white" />
          </button>
          <img src={imgUrl} alt="" style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }} />
        </div>
      )}

      <div className="fixed inset-0 z-50 flex items-end justify-center"
           style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onClose}>

        {/* Off-screen card for capture */}
        <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', pointerEvents: 'none' }}>
          <TarotShareCard ref={cardRef} data={{ ...data, imageDataUrl: cardImageDataUrl }} />
        </div>

        {toast && (
          <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-2xl text-sm font-semibold"
               style={{ background: 'rgba(30,25,16,0.95)', color: '#d4b884', border: '1px solid rgba(184,148,90,0.3)' }}>
            {toast}
          </div>
        )}

        <div className="w-full max-w-md rounded-t-3xl pb-8"
             style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>

          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-surface-3)' }} />
          </div>

          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex items-center gap-2">
              <Share2 size={18} style={{ color: 'var(--color-gold-dark)' }} />
              <h3 className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>แชร์ไพ่วันนี้</h3>
            </div>
            <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
          </div>

          {/* Preview */}
          <div className="mx-5 mb-5 rounded-2xl overflow-hidden flex items-center justify-center"
               style={{ background: '#16120d', height: '260px', cursor: imgUrl ? 'zoom-in' : 'default' }}
               onClick={() => imgUrl && setFullscreen(true)}>
            {generating ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-full animate-spin"
                     style={{ border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#d4b884' }} />
                <span className="text-xs" style={{ color: '#b8b09a' }}>กำลังสร้างภาพ...</span>
              </div>
            ) : imgUrl ? (
              <div className="relative h-full flex items-center justify-center">
                <img src={imgUrl} alt="" className="h-full w-auto object-contain" style={{ maxWidth: '100%' }} />
                <div className="absolute bottom-2 right-2 px-2 py-1 rounded-lg text-[10px] font-semibold"
                     style={{ background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.7)' }}>
                  กดเพื่อดูเต็มจอ
                </div>
              </div>
            ) : (
              <span className="text-xs" style={{ color: '#7a7260' }}>ไม่สามารถสร้างภาพได้</span>
            )}
          </div>

          {/* Platform buttons */}
          <div className="px-5 mb-3">
            <p className="text-[10px] text-center mb-3 font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              แชร์ไปยัง
            </p>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => { showToast('เลือก Facebook ในเมนูที่เปิด'); handleNativeShare() }}
                disabled={generating || sharing}
                className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: 'var(--color-surface-2)' }}>
                <FbLogo />
                <span className="text-[11px] font-semibold" style={{ color: 'var(--color-text)' }}>Facebook</span>
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>Story / Post</span>
              </button>
              <button
                onClick={() => { showToast('เลือก Instagram ในเมนูที่เปิด'); handleNativeShare() }}
                disabled={generating || sharing}
                className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: 'var(--color-surface-2)' }}>
                <IgLogo />
                <span className="text-[11px] font-semibold" style={{ color: 'var(--color-text)' }}>Instagram</span>
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>Story / Post</span>
              </button>
              <button
                onClick={handleLineShare}
                disabled={generating || lineSharing}
                className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: 'var(--color-surface-2)' }}>
                <LineLogo />
                <span className="text-[11px] font-semibold" style={{ color: 'var(--color-text)' }}>LINE</span>
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>
                  {lineSharing ? 'กำลังส่ง...' : 'เลือกเพื่อน'}
                </span>
              </button>
            </div>
          </div>

          <div className="px-5">
            <button
              onClick={handleDownload}
              disabled={!imgUrl}
              className="w-full py-3 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-40"
              style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
              <Download size={15} />
              บันทึกรูปลงเครื่อง
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
